import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listInvestorOrganizations, listInvestorPackages } from '../../api/investorApi';
import { DataTable } from '../../components/ui/DataTable';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { FilterSelect } from '../../components/ui/FilterSelect';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { formatDateTime } from '../../utils/format';
import { PACKAGE_STATUS_OPTIONS, humanize } from './investorLabels';

export function InvestorPackagesPage() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState('');
  const [organizationId, setOrganizationId] = useState(searchParams.get('organization') || '');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listInvestorOrganizations({ pageSize: 100 })
      .then((response) => {
        if (!cancelled) setOrganizations(response?.items || []);
      })
      .catch(() => {
        // the org filter is optional; the table still loads
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listInvestorPackages({
          page,
          pageSize,
          status,
          organizationId,
          q: query,
        });
        if (cancelled) return;
        setRows(response?.items || []);
        setTotal(response?.meta?.total ?? 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load packages.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, status, organizationId, query, reloadToken]);

  const orgOptions = useMemo(
    () => [
      { value: '', label: 'All organizations' },
      ...organizations
        .filter((org) => org.org_type === 'originator' || org.org_type === 'custodian')
        .map((org) => ({ value: org.id, label: `${org.name} (${humanize(org.org_type)})` })),
    ],
    [organizations]
  );

  const columns = useMemo(
    () => [
      {
        key: 'reference_number',
        header: 'Package',
        render: (row) => (
          <div className="min-w-0">
            <Link
              to={`/investor/packages/${row.package_id}`}
              onClick={(e) => e.stopPropagation()}
              className="block font-semibold text-eorig-ink hover:underline"
            >
              {row.reference_number || row.package_id}
            </Link>
            <span className="block text-[11px] text-eorig-muted">{formatDateTime(row.created_at)}</span>
          </div>
        ),
      },
      { key: 'originator', header: 'Originator' },
      { key: 'program', header: 'Program' },
      { key: 'custodian', header: 'Custodian', render: (row) => row.custodian || '—' },
      { key: 'items_count', header: 'Assets', align: 'right' },
      {
        key: 'package_status',
        header: 'Status',
        render: (row) => <StatusPill value={row.package_status} label={humanize(row.package_status)} />,
      },
      {
        key: 'authority_status',
        header: 'Authority',
        render: (row) => <StatusPill value={row.authority_status} label={humanize(row.authority_status)} />,
      },
      {
        key: 'custody_status',
        header: 'Custody',
        render: (row) => <StatusPill value={row.custody_status} label={humanize(row.custody_status)} />,
      },
      {
        key: 'updated_at',
        header: 'Last change',
        render: (row) => <span className="text-eorig-muted">{formatDateTime(row.updated_at)}</span>,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setQuery(search.trim());
        }}
      >
        <input
          type="search"
          aria-label="Search packages"
          placeholder="Search reference, program or originator"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="h-9 w-72 rounded-md border border-[#D1D5DB] bg-white px-3 text-sm text-[#1F2933] focus:outline-none focus:ring-2 focus:ring-[#00A6D6]/30"
        />
        <FilterSelect
          label="Status"
          ariaLabel="Filter by package status"
          value={status}
          onChange={(value) => {
            setPage(1);
            setStatus(value);
          }}
          options={PACKAGE_STATUS_OPTIONS}
        />
        <FilterSelect
          label="Organization"
          ariaLabel="Filter by organization"
          value={organizationId}
          onChange={(value) => {
            setPage(1);
            setOrganizationId(value);
          }}
          options={orgOptions}
        />
      </form>

      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((v) => v + 1)} /> : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.package_id}
        rowHref={(row) => `/investor/packages/${row.package_id}`}
        loading={loading}
        emptyMessage="No packages match these filters."
        minWidth={960}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
